import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldIcon, TruckIcon, ClockIcon, MapPinIcon, DropletsIcon, WindIcon, CarIcon, TractorIcon, PaintbrushIcon, AnchorIcon, BrickWallIcon, LayersIcon, TrainIcon, ZapIcon, LeafIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function HomePage() {
  return <div className="w-full overflow-hidden" data-id="element-257">
      <HeroSection data-id="element-258" />
      <TrustBar data-id="element-259" />
      <ServicesSection data-id="element-260" />
      <IndustriesSection data-id="element-261" />
      <WhyChooseSection data-id="element-262" />
      <GalleryPreviewSection data-id="element-263" />
      <ProcessSection data-id="element-264" />
      <ServiceAreaSection data-id="element-265" />
      <TestimonialsSection data-id="element-266" />
      <FAQSection data-id="element-267" />
      <FinalCTASection data-id="element-268" />
    </div>;
}
function HeroSection() {
  return <section className="relative min-h-[580px] md:min-h-[650px] flex items-center justify-center bg-charcoal" data-id="element-269">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-270">
        <img src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=1920&auto=format&fit=crop" alt="Industrial surface preparation and blasting" className="w-full h-full object-cover opacity-40" data-id="element-271" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/60 to-charcoal/90" data-id="element-272"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 w-full pt-16 pb-28" data-id="element-273">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl" data-id="element-274">
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl lg:text-7xl font-heading font-extrabold text-white uppercase leading-[1.1] tracking-tight mb-6" data-id="element-275">
            Mobile Dustless &amp; Dry Blasting Services{' '}
            <span className="text-brand-red" data-id="element-276">Across Michigan</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-steel-200 font-body mb-10 max-w-2xl leading-relaxed" data-id="element-277">
            Fowlerville-based. Statewide service. We bring professional surface
            preparation directly to your site — fast estimates, fast turnaround,
            less mess.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-278">
            <Button variant="primary" size="lg" href="tel:5177778088" showPhoneIcon className="w-full sm:w-auto" data-id="element-279">
              Call (517) 777-8088
            </Button>
            <a href="#quote" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 border-2 border-white text-white bg-transparent hover:bg-white hover:text-charcoal transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-280">
              Get a Free Quote
            </a>
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-8" data-id="element-281">
            <a href="https://www.facebook.com/meremblasting/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-steel-300 hover:text-white transition-colors text-sm font-medium" data-id="element-282">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-283">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-284" />
              </svg>
              Message Us on Facebook
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}
function TrustBar() {
  const items = [{
    icon: ShieldIcon,
    text: 'Low-Mess Dustless Process'
  }, {
    icon: TruckIcon,
    text: 'Mobile, On-Site Service'
  }, {
    icon: ClockIcon,
    text: 'Fast Turnaround'
  }, {
    icon: MapPinIcon,
    text: 'All of Michigan'
  }];
  return <div className="bg-steel-50 border-b border-steel-200 py-6" data-id="element-285">
      <div className="max-w-7xl mx-auto px-4" data-id="element-286">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8" data-id="element-287">
          {items.map((item, idx) => <div key={idx} className="flex items-center justify-center md:justify-start space-x-3" data-id="element-288">
              <item.icon className="w-6 h-6 text-brand-red flex-shrink-0" data-id="element-289" />
              <span className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm md:text-base" data-id="element-290">
                {item.text}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
function ServicesSection() {
  const services = [{
    icon: DropletsIcon,
    title: 'Dustless Blasting',
    desc: 'Low-mess wet blasting that suppresses dust and cleans surfaces without damage to surrounding areas.'
  }, {
    icon: WindIcon,
    title: 'Dry Blasting',
    desc: 'Traditional abrasive blasting for heavy-duty coating removal and surface preparation.'
  }, {
    icon: CarIcon,
    title: 'Automotive Restoration',
    desc: 'Strip paint, rust, and body filler from cars, trucks, and classic vehicles down to bare metal.'
  }, {
    icon: TractorIcon,
    title: 'Heavy Equipment Cleaning',
    desc: 'Remove rust, paint, and buildup from construction, farm, and industrial machinery.'
  }, {
    icon: PaintbrushIcon,
    title: 'Graffiti Removal',
    desc: 'Restore brick, concrete, and stone surfaces by removing graffiti without damaging the substrate.'
  }, {
    icon: AnchorIcon,
    title: 'Boat & Marine Prep',
    desc: 'Strip bottom paint, gelcoat, and marine coatings for refinishing and restoration.'
  }, {
    icon: BrickWallIcon,
    title: 'Concrete & Brick Restoration',
    desc: 'Clean, etch, and restore concrete and masonry surfaces for commercial and residential projects.'
  }, {
    icon: LayersIcon,
    title: 'Powder Coating Removal',
    desc: 'Efficiently strip powder coating from metal parts, frames, and equipment.'
  }, {
    icon: TrainIcon,
    title: 'Rail Car Blasting',
    desc: 'Blast and prepare rail cars and rolling stock for repainting and maintenance.'
  }];
  return <section className="py-20 bg-white" id="services" data-id="element-291">
      <div className="max-w-7xl mx-auto px-4" data-id="element-292">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-293">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-294">
            Our Blasting &amp; Surface Preparation Services
          </h2>
          <p className="text-lg text-steel-600 font-body" data-id="element-295">
            From automotive restoration to industrial equipment, we strip
            coatings, remove rust, and prepare surfaces the right way.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-296">
          {services.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100 hover:border-brand-red hover:shadow-lg transition-all duration-300 group" data-id="element-297">
              <service.icon className="w-10 h-10 text-brand-red mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-298" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-299">
                {service.title}
              </h3>
              <p className="text-steel-600 mb-6 text-sm leading-relaxed" data-id="element-300">
                {service.desc}
              </p>
              <a href="#contact" className="inline-flex items-center text-brand-red font-bold text-sm uppercase tracking-wider hover:text-brand-red-dark" data-id="element-301">
                Learn More{' '}
                <span className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-302">
                  →
                </span>
              </a>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
function IndustriesSection() {
  const industries = ['Automotive', 'Construction', 'Agriculture', 'Commercial Properties', 'Industrial & Manufacturing', 'Gas & Oil', 'Aviation & Aerospace', 'Railway', 'Marine'];
  return <section className="py-16 bg-charcoal text-white overflow-hidden" data-id="element-303">
      <div className="max-w-7xl mx-auto px-4" data-id="element-304">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-305">
          <h2 className="text-2xl md:text-4xl font-heading font-extrabold uppercase mb-10 text-center" data-id="element-306">
            Industries We Serve
          </h2>

          {/* Mobile horizontal scroll, Desktop grid */}
          <div className="flex overflow-x-auto pb-6 md:pb-0 md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 hide-scrollbar" data-id="element-307">
            {industries.map((ind, idx) => <div key={idx} className="flex-shrink-0 w-48 md:w-auto bg-steel-900 border border-steel-800 rounded-sm p-4 text-center hover:bg-brand-red hover:border-brand-red transition-colors cursor-default" data-id="element-308">
                <span className="font-heading font-semibold tracking-wide uppercase text-sm" data-id="element-309">
                  {ind}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}
function WhyChooseSection() {
  const features = [{
    icon: ZapIcon,
    title: 'Fast Estimates & Turnaround',
    desc: 'We respond quickly and get the job done on your timeline. No weeks of waiting.'
  }, {
    icon: ShieldIcon,
    title: 'OSHA-Conscious & Safety-Forward',
    desc: 'Our processes prioritize crew safety and site compliance.'
  }, {
    icon: LeafIcon,
    title: 'Environmentally Responsible',
    desc: 'Dustless blasting requires less containment and is EPA/DEQ-conscious. Better for your site and the environment.'
  }, {
    icon: TruckIcon,
    title: 'Fully Mobile, Statewide',
    desc: 'We come to you anywhere in Michigan. No need to transport your equipment or project.'
  }];
  return <section className="py-20 bg-white" data-id="element-310">
      <div className="max-w-7xl mx-auto px-4" data-id="element-311">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-312">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-313">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-10" data-id="element-314">
              Why Michigan Trusts{' '}
              <span className="text-brand-red" data-id="element-315">Merem Blasting</span>
            </h2>

            <div className="space-y-8" data-id="element-316">
              {features.map((feat, idx) => <motion.div key={idx} variants={fadeInUp} className="flex" data-id="element-317">
                  <div className="flex-shrink-0 mt-1" data-id="element-318">
                    <div className="w-12 h-12 bg-steel-50 rounded-full flex items-center justify-center border border-steel-200" data-id="element-319">
                      <feat.icon className="w-6 h-6 text-brand-red" data-id="element-320" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-321">
                    <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-2" data-id="element-322">
                      {feat.title}
                    </h3>
                    <p className="text-steel-600 leading-relaxed" data-id="element-323">
                      {feat.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="order-first lg:order-last h-64 md:h-96 lg:h-[600px] relative rounded-sm overflow-hidden shadow-2xl" data-id="element-324">
            <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000&auto=format&fit=crop" alt="Industrial worker in safety gear" className="w-full h-full object-cover" data-id="element-325" />
            <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-326"></div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function GalleryPreviewSection() {
  const projects = [{
    img: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=800&auto=format&fit=crop',
    label: 'Classic Car Frame — Fowlerville, MI'
  }, {
    img: 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?q=80&w=800&auto=format&fit=crop',
    label: 'Industrial Equipment — Lansing, MI'
  }, {
    img: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=800&auto=format&fit=crop',
    label: 'Brick Wall Graffiti Removal — Detroit, MI'
  }];
  return <section className="py-20 bg-steel-50" data-id="element-327">
      <div className="max-w-7xl mx-auto px-4" data-id="element-328">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-329">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-2xl" data-id="element-330">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-331">
              See the Results
            </h2>
            <p className="text-lg text-steel-600 font-body" data-id="element-332">
              Real projects. Real transformations. No stock photos.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-6 md:mt-0" data-id="element-333">
            <Button variant="secondary" data-id="element-334">View Full Gallery</Button>
          </motion.div>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-335">
          {projects.map((proj, idx) => <motion.div key={idx} variants={fadeInUp} className="group cursor-pointer" data-id="element-336">
              <div className="relative h-64 md:h-80 overflow-hidden rounded-sm mb-4 bg-charcoal" data-id="element-337">
                <img src={proj.img} alt={proj.label} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" data-id="element-338" />
                <div className="absolute top-4 left-4 bg-brand-red text-white text-xs font-bold uppercase px-3 py-1 tracking-wider" data-id="element-339">
                  Before / After
                </div>
              </div>
              <h3 className="font-heading font-bold text-lg text-charcoal uppercase" data-id="element-340">
                {proj.label}
              </h3>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
function ProcessSection() {
  const steps = [{
    num: '01',
    title: 'Request a Quote',
    desc: 'Call us or fill out our quick form. Tell us about your project, surface type, and timeline.'
  }, {
    num: '02',
    title: 'Project Review',
    desc: 'We review your project details, ask any follow-up questions, and provide a clear estimate.'
  }, {
    num: '03',
    title: 'On-Site Blasting',
    desc: 'Our mobile crew arrives at your location with all equipment. We blast, clean, and prep your surfaces.'
  }, {
    num: '04',
    title: 'Ready for Next Steps',
    desc: 'Your surface is prepped and ready for coating, painting, or whatever comes next.'
  }];
  return <section className="py-20 bg-white" data-id="element-341">
      <div className="max-w-7xl mx-auto px-4" data-id="element-342">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-16" data-id="element-343">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal" data-id="element-344">
            How It Works
          </h2>
        </motion.div>

        <div className="relative" data-id="element-345">
          {/* Desktop connecting line */}
          <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-steel-100 z-0" data-id="element-346"></div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-4 gap-10 relative z-10" data-id="element-347">
            {steps.map((step, idx) => <motion.div key={idx} variants={fadeInUp} className="relative" data-id="element-348">
                <div className="w-16 h-16 bg-white border-4 border-brand-red rounded-full flex items-center justify-center font-heading font-extrabold text-2xl text-brand-red mb-6 mx-auto md:mx-0 shadow-sm" data-id="element-349">
                  {step.num}
                </div>
                <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3 text-center md:text-left" data-id="element-350">
                  {step.title}
                </h3>
                <p className="text-steel-600 text-sm leading-relaxed text-center md:text-left" data-id="element-351">
                  {step.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}
function ServiceAreaSection() {
  const cities = ['Detroit Metro', 'Lansing / East Lansing', 'Grand Rapids', 'Ann Arbor', 'Flint', 'Kalamazoo', 'Traverse City', 'Saginaw / Bay City', 'Fowlerville (HQ)'];
  return <section className="py-20 bg-charcoal text-white relative overflow-hidden" data-id="element-352">
      {/* Abstract Map Background Graphic */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-5 pointer-events-none hidden lg:block" data-id="element-353">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-354">
          <path fill="#FFFFFF" d="M45.7,-76.1C58.9,-69.3,69.1,-55.4,77.3,-40.8C85.5,-26.2,91.8,-10.9,89.6,3.4C87.4,17.7,76.8,31,66.1,43.2C55.4,55.4,44.7,66.5,31.4,73.1C18.1,79.7,2.2,81.8,-12.8,79.5C-27.8,77.2,-41.9,70.5,-53.4,60.6C-64.9,50.7,-73.8,37.6,-79.8,22.9C-85.8,8.2,-88.9,-8.1,-84.6,-22.3C-80.3,-36.5,-68.6,-48.6,-55.3,-55.5C-42,-62.4,-27.1,-64.1,-12.4,-67.6C2.3,-71.1,17,-76.4,32.4,-78.9C47.8,-81.4,63.9,-77.1,45.7,-76.1Z" transform="translate(100 100)" data-id="element-355" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-356">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-357">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-358">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-359">
              Serving All of <span className="text-brand-red" data-id="element-360">Michigan</span>
            </h2>
            <p className="text-lg text-steel-300 font-body mb-10 max-w-lg" data-id="element-361">
              Based in Fowlerville. Mobile across the entire state. We regularly
              serve these regions and everywhere in between.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10" data-id="element-362">
              {cities.map((city, idx) => <div key={idx} className="flex items-center space-x-3" data-id="element-363">
                  <MapPinIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-364" />
                  <span className="font-heading font-semibold tracking-wide text-lg" data-id="element-365">
                    {city}
                  </span>
                </div>)}
            </div>

            <div className="bg-steel-900 border-l-4 border-brand-red p-6" data-id="element-366">
              <p className="font-bold text-white mb-2" data-id="element-367">Not listed?</p>
              <p className="text-steel-300 text-sm mb-4" data-id="element-368">
                We probably cover your area. Call to confirm.
              </p>
              <Button variant="primary" size="sm" href="tel:5177778088" showPhoneIcon data-id="element-369">
                Call to Confirm
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function TestimonialsSection() {
  const testimonials = [{
    quote: 'Merem Blasting came out to our facility and stripped three pieces of heavy machinery in a fraction of the time it would have taken our crew. Zero mess left behind.',
    name: 'Industrial Facility Manager',
    project: 'Heavy Equipment Cleaning',
    loc: 'Grand Rapids, MI'
  }, {
    quote: 'We needed the brick on our historic building cleaned without damaging the mortar. The dustless process was perfect, and the team was incredibly professional.',
    name: 'Commercial Property Owner',
    project: 'Brick Restoration',
    loc: 'Detroit, MI'
  }, {
    quote: "Stripped my '68 Camaro frame down to bare metal perfectly. They showed up on time, got it done fast, and the surface was ready for primer immediately.",
    name: 'Auto Restoration Enthusiast',
    project: 'Automotive Frame Blasting',
    loc: 'Lansing, MI'
  }];
  return <section className="py-20 bg-white" data-id="element-370">
      <div className="max-w-7xl mx-auto px-4" data-id="element-371">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-16" data-id="element-372">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-373">
            What Our Clients Say
          </h2>
          <p className="text-steel-500 text-sm italic" data-id="element-374">
            Project highlights based on real work. Contact us for references.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-375">
          {testimonials.map((test, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100 relative" data-id="element-376">
              <div className="absolute top-4 right-6 text-6xl font-heading text-steel-200 opacity-50" data-id="element-377">
                "
              </div>
              <p className="text-steel-700 italic mb-8 relative z-10 leading-relaxed" data-id="element-378">
                "{test.quote}"
              </p>
              <div data-id="element-379">
                <p className="font-heading font-bold uppercase text-charcoal text-lg" data-id="element-380">
                  {test.name}
                </p>
                <p className="text-brand-red text-sm font-semibold" data-id="element-381">
                  {test.project}
                </p>
                <p className="text-steel-500 text-xs mt-1" data-id="element-382">{test.loc}</p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: 'What is dustless blasting?',
    a: 'Dustless blasting uses a mixture of water, air, and abrasive media to remove coatings, rust, and contaminants from surfaces. The water suppresses dust up to 95%, making it cleaner and safer than traditional sandblasting. It requires less containment and is EPA/DEQ-conscious.'
  }, {
    q: "What's the difference between dustless and dry blasting?",
    a: 'Dustless (wet) blasting adds water to suppress dust and reduce heat, making it ideal for sensitive surfaces and populated areas. Dry blasting uses abrasive media without water for maximum aggression on heavy rust and coatings. We offer both so we can match the right process to your project.'
  }, {
    q: 'Do you come to my location?',
    a: 'Yes. We are a fully mobile operation based in Fowlerville, Michigan. We bring all equipment to your site anywhere in the state. No need to transport your vehicle, equipment, or project to us.'
  }, {
    q: 'How fast can you start?',
    a: 'We pride ourselves on fast estimates and fast turnaround. Most quotes are returned within 24-48 hours, and scheduling depends on current workload. Call us at (517) 777-8088 for the fastest response.'
  }, {
    q: 'What surfaces can you blast?',
    a: "Metal, steel, aluminum, concrete, brick, stone, wood, fiberglass, and more. We work on vehicles, heavy equipment, buildings, boats, rail cars, and industrial components. If you're unsure, just ask."
  }, {
    q: 'Is dustless blasting safe for my vehicle or equipment?',
    a: "Yes. Dustless blasting is gentler than traditional sandblasting because the water cushions the abrasive impact. It won't warp sheet metal and is safe for automotive panels, frames, and components."
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section className="py-20 bg-steel-50" data-id="element-383">
      <div className="max-w-4xl mx-auto px-4" data-id="element-384">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-12" data-id="element-385">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal" data-id="element-386">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-387">
          {faqs.map((faq, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white border border-steel-200 rounded-sm overflow-hidden" data-id="element-388">
              <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red" onClick={() => setOpenIdx(openIdx === idx ? null : idx)} aria-expanded={openIdx === idx} data-id="element-389">
                <span className="font-heading font-bold text-lg uppercase text-charcoal pr-8" data-id="element-390">
                  {faq.q}
                </span>
                {openIdx === idx ? <ChevronUpIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-391" /> : <ChevronDownIcon className="w-5 h-5 text-steel-400 flex-shrink-0" data-id="element-392" />}
              </button>
              <AnimatePresence data-id="element-393">
                {openIdx === idx && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-394">
                    <div className="px-6 pb-6 text-steel-600 leading-relaxed" data-id="element-395">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function FinalCTASection() {
  return <section className="py-24 bg-brand-red text-white text-center px-4" data-id="element-396">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-397">
        <h2 className="text-4xl md:text-6xl font-heading font-extrabold uppercase mb-6" data-id="element-398">
          Ready to Get Started?
        </h2>
        <p className="text-xl font-body mb-10 text-brand-red-light text-white/90" data-id="element-399">
          Call for a fast estimate or fill out our quote form. We'll get back to
          you quickly.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8" data-id="element-400">
          <Button variant="secondary" size="lg" href="tel:5177778088" showPhoneIcon className="w-full sm:w-auto border-none" data-id="element-401">
            Call (517) 777-8088
          </Button>
          <a href="#quote" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 border-2 border-white text-white bg-transparent hover:bg-white hover:text-brand-red transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-402">
            Request a Quote
          </a>
        </div>
        <p className="text-sm font-bold tracking-widest uppercase opacity-80" data-id="element-403">
          Serving all of Michigan from Fowlerville
        </p>
      </motion.div>
    </section>;
}